import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageService, MART_USER_CART, ProductDTO, CartItemDTO, interceptorsToken, ActivityIndicatorType, HttpHandlerService, ComponentsService } from 'library-explorer';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private martCart$ = new BehaviorSubject<CartItemDTO[]>([]);
  private cartOrderSummary$ = new BehaviorSubject<any>(null);
  private isAllChecked$ = new BehaviorSubject<boolean>(false);
  public selectedCartItems = new BehaviorSubject<CartItemDTO[]>([]);

  constructor(private storageService: StorageService, private components: ComponentsService, private httpWrapper: HttpHandlerService) { }

  selectedCartItems$ = this.selectedCartItems.asObservable();

  get getAllCartItems(): Observable<CartItemDTO[]> {
    return this.martCart$.asObservable();
  }

  get getCartOrderSummary(): Observable<any> {
    return this.cartOrderSummary$.asObservable();
  }

  get getIsAllChecked(): Observable<boolean> {
    return this.isAllChecked$.asObservable();
  }

  get getSelectedItemsValue() {
    return this.selectedCartItems.value;
  }

  init() {
    this.getCartItems();
  }

  emptyCart() {
    this.martCart$.next([]);
  }

  calculateCartOrderSummary(cartItems: CartItemDTO[]) {
    if (!cartItems || !cartItems.length) return;

    let sumTotalBeforeDiscount = 0;
    let sumTotalAfterDiscount = 0;
    let sumShippingFee = 0;
    let amountSaved = 0;
    let noOfItems = 0;

    cartItems
      .filter(item => item.selected && item.product.quantity > 0)
      .forEach(item => {
        const { price, discount, shipping_fee } = item.product;

        const shippingFee = Number(shipping_fee || 0);
        const itemTotalBeforeDiscount = price * item.quantity;
        const itemTotalAfterDiscount = item.total_price + shippingFee;

        sumTotalBeforeDiscount += itemTotalBeforeDiscount;
        sumTotalAfterDiscount += itemTotalAfterDiscount;
        amountSaved += itemTotalBeforeDiscount - itemTotalAfterDiscount + shippingFee;
        noOfItems += item.quantity;
        sumShippingFee += shippingFee;
      });

    const cartOrderSummary = {
      sumTotalBeforeDiscount,
      sumTotalAfterDiscount,
      sumShippingFee,
      amountSaved,
      noOfItems
    };

    this.cartOrderSummary$.next(cartOrderSummary);
  }

  async updateCartItemQuantity(productId: number, quantity: number) {
    if (quantity <= 0) return;

    let cartItems: string | null | CartItemDTO[] = await this.storageService.check4Value(MART_USER_CART);

    if (cartItems) {
      cartItems = JSON.parse(cartItems) as CartItemDTO[];

      const existingCartItemIndex = cartItems.findIndex((item: CartItemDTO) => item.product_id === productId);

      if (existingCartItemIndex !== -1) {
        const item = cartItems[existingCartItemIndex];

        item.quantity = quantity;
        item.total_price = item.unit_price * quantity;

        await this.updateCartStorageValue(cartItems);
      }
    }
  }

  async addToCart(product: ProductDTO, quantity: number = 1) {

    if (!product || quantity <= 0) return;

    // Calculate unit price and total price
    const unit_price = product.price - product.discount;
    const total_price = unit_price * quantity;

    // Create the CartItemDTO object
    const cartItem: CartItemDTO = {
      product,
      product_id: product.id,
      quantity,
      unit_price,
      total_price,
      selected: true
    };

    // Retrieve the existing cart items from storage
    let cartItems: string | null | CartItemDTO[] = await this.storageService.check4Value(MART_USER_CART);

    if (cartItems) {
      cartItems = JSON.parse(cartItems) as CartItemDTO[];

      // Check if the product already exists in the cart
      const existingCartItemIndex = cartItems.findIndex((item: CartItemDTO) => item.product_id === product.id);

      if (existingCartItemIndex !== -1) {
        // Update the quantity and total price if the product is already in the cart
        cartItems[existingCartItemIndex].quantity += quantity;
        cartItems[existingCartItemIndex].total_price += total_price;
      } else {
        // Add the new cart item to the cart
        cartItems.push(cartItem);
      }
    } else {
      // If there are no existing items, create a new cart with the current item
      cartItems = [cartItem];
    }

    // Store the updated cart items in storage
    await this.updateCartStorageValue(cartItems);
    this.components.presentToast('Added to cart');
  }

  async removeFromCart(product: ProductDTO) {
    if (!product) return;

    let cartItems: string | null | CartItemDTO[] = await this.storageService.check4Value(MART_USER_CART);
    if (cartItems) {
      cartItems = JSON.parse(cartItems) as CartItemDTO[]
      const cartIndex = cartItems.findIndex(
        (item: CartItemDTO) => item.product_id === product.id
      );
      if (cartIndex !== -1) {
        cartItems.splice(cartIndex, 1);
        await this.updateCartStorageValue(cartItems);
      }
    }
    this.getCartItems();
  }

  async isProductInCart(product: ProductDTO): Promise<boolean> {
    try {
      const cartItems = await this.storageService.check4Value(MART_USER_CART);
      if (!cartItems) return false;

      const parsedCartItems = JSON.parse(cartItems);
      return parsedCartItems.some((item: CartItemDTO) => item.product_id === product.id);
    } catch {
      return false;
    }
  }

  async unselectCartItem(cartItem: CartItemDTO) {
    let cartItems: CartItemDTO[] = await this.getCartItemsFromStorage();
    cartItems = cartItems.map(item =>
      item.product_id === cartItem.product_id ? { ...item, selected: false } : item
    );
    await this.saveCartItemsToStorage(cartItems);
  }

  async selectCartItem(cartItem: CartItemDTO) {
    let cartItems: CartItemDTO[] = await this.getCartItemsFromStorage();
    cartItems = cartItems.map(item =>
      item.product_id === cartItem.product_id ? { ...item, selected: true } : item
    );
    await this.saveCartItemsToStorage(cartItems);
  }

  async selectAllCartItems(status: boolean) {
    let cartItems: CartItemDTO[] = await this.getCartItemsFromStorage();
    cartItems = cartItems.map(item => ({ ...item, selected: status }));
    this.martCart$.next(cartItems);
    await this.saveCartItemsToStorage(cartItems);
  }

  private async confirmAllChecked(cartItems: CartItemDTO[]) {
    // check if all items in the cart is checked
    const allItemsSelected = cartItems.every(item => item.selected && item.product.quantity > 0);
    this.isAllChecked$.next(allItemsSelected);
  }

  private async getCartItems() {
    this.getRemoteCart().pipe(
      tap(async (cartItems: CartItemDTO[]) => {
        await this.storageService.setStorageValue(MART_USER_CART, JSON.stringify(cartItems));
        this.martCart$.next(cartItems);
        this.cleanUpCartSummary(cartItems);
      })
    ).subscribe();
  }

  private async getCartItemsFromStorage(): Promise<CartItemDTO[]> {
    const cartItems = await this.storageService.check4Value(MART_USER_CART);
    return cartItems ? JSON.parse(cartItems) : [];
  }

  private async saveCartItemsToStorage(cartItems: CartItemDTO[]) {
    await this.updateCartStorageValue(cartItems);
    await this.cleanUpCartSummary(cartItems);
  }

  private async cleanUpCartSummary(cartItems: CartItemDTO[]) {
    this.calculateCartOrderSummary(cartItems);
    this.updateSelectedCartItems(cartItems);

    await this.confirmAllChecked(cartItems)
  }

  private async updateSelectedCartItems(cartItems: CartItemDTO[]) {
    const selectedItems = cartItems.filter(item => item.selected && item.product.quantity > 0);
    this.selectedCartItems.next(selectedItems);
  }

  async updateCartStorageValue(cartItems: CartItemDTO[]) {
    this.updateRemoteCart(cartItems).subscribe(async (cartItems: CartItemDTO[]) => {
      this.martCart$.next(cartItems);
      await this.storageService.setStorageValue(MART_USER_CART, JSON.stringify(cartItems));
      this.cleanUpCartSummary(cartItems);
    });
  }

  getRemoteCart(): Observable<CartItemDTO[]> {
    return this.httpWrapper.getRequest<CartItemDTO[]>(`carts`);
  }

  updateRemoteCart(cartItems: CartItemDTO[]): Observable<CartItemDTO[]> {
    const headers = new HttpHeaders().set(interceptorsToken.ACTIVITY_INDICATOR_TYPE, ActivityIndicatorType.FullScreen);
    return this.httpWrapper.postRequest<CartItemDTO[]>(`carts`, { cart_items: cartItems }, undefined, headers);
  }
}
